import { NgZone } from '@angular/core';
import { Subscription } from 'rxjs';
import { ProjectService } from 'src/app/core/services/project.service';
import * as THREE from 'three';
import { LineBasicMaterial, LineSegments, Vector2 } from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Load } from './load';
import RenderingContext from './rendering-context';
import { MainView } from './main-view';

export class LoadOutline {
  public mouseMoveListener: EventListener;

  public isActive: boolean;
  private selectedObjects: Load[] = [];
  private selectedObject: Load;
  private projectSubscription: Subscription;
  private mousePosition = new Vector2();

  constructor(
    private renderingContext: RenderingContext<any>,
    private loads: Load[],
    private projectService: ProjectService,
    private zone: NgZone,
    private mainView: MainView,
  ) {
    this.createEventListeners();
    this.projectSubscription = this.projectService.currentLoadGroupId.subscribe(
      (currentLoadGroup: { id: number; object?: Load }) =>
        this.outlineLoadsFromGroup(currentLoadGroup.id, currentLoadGroup.object),
    );
  }

  public disableOutline() {
    if (this.projectSubscription) {
      this.projectSubscription.unsubscribe();
    }

    this.deleteEventListeners();
  }

  public update(renderingContext: RenderingContext<any>, loads: Load[]) {
    this.loads = loads;
    this.renderingContext = renderingContext;
  }

  private createEventListeners() {
    if (!this.isActive) {
      this.zone.runOutsideAngular(() => {
        this.renderingContext.renderer.domElement.addEventListener(
          'pointermove',
          (this.mouseMoveListener = this.onPointerMove.bind(this) as EventListener),
        );
      });
      this.isActive = true;
    }
  }

  private deleteEventListeners() {
    if (this.isActive) {
      this.zone.runOutsideAngular(() => {
        this.renderingContext.renderer.domElement.removeEventListener(
          'pointermove',
          this.mouseMoveListener,
          false,
        );
      });
      this.isActive = false;
    }
  }

  private outlineLoadsFromGroup(id: number, object?: Load) {
    if (this.selectedObjects) {
      this.selectedObjects.forEach((load: Load) => {
        this.deleteOutlineObject(load);
      });
    }
    this.selectedObjects = [];
    if (object) {
      this.addSelectedObject(object);
    } else if (id && this.renderingContext.scene.getObjectByName('loadsGroup') && !object) {
      const loadsGroup = this.renderingContext.scene.getObjectByName('loadsGroup');
      loadsGroup.children.forEach((load: Load) => {
        if (load.idGroup === id) {
          this.addSelectedObject(load);
        }
      });
    }
  }

  private onPointerMove(event: TouchEvent & MouseEvent) {
    let x = 0;
    let y = 0;

    if (event.changedTouches) {
      x = event.changedTouches[0].pageX;
      y = event.changedTouches[0].pageY;
    } else {
      x = event.clientX;
      y = event.clientY;
    }
    const rect: ClientRect = this.renderingContext.renderer.domElement.getBoundingClientRect();
    this.mousePosition.x = ((x - rect.left) / rect.width) * 2 - 1;
    this.mousePosition.y = -((y - rect.top) / rect.height) * 2 + 1;

    this.checkIntersection();
  }

  private checkIntersection() {
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(this.mousePosition, this.renderingContext.camera as THREE.Camera);
    const intersects = raycaster.intersectObjects(this.loads, true);
    if (this.selectedObject) {
      this.deleteOutlineObject(this.selectedObject);
      this.selectedObject = undefined;
    }
    if (intersects.length > 0) {
      this.selectedObject = intersects[0].object as Load;
      this.projectService.setCurrentLoadGroupId(this.selectedObject.idGroup, this.selectedObject);
      this.mainView.startShortAnimation();
    } else {
      this.projectService.setCurrentLoadGroupId(undefined);
    }
  }

  private addSelectedObject(object: Load) {
    const line = this.getLine(object);
    const materialLine = line.material as LineMaterial;
    materialLine.color = new THREE.Color(0x4eb3ff);
    materialLine.linewidth = 0.001;
    this.selectedObjects.push(object);
  }

  private getLine(object: Load): LineSegments {
    const isPipeOrBarell = object.loadType === 2 || object.loadType === 1;
    return isPipeOrBarell
      ? (object.children
          .find((obj) => obj.name === 'LineSegments')
          .children.find((obj) => obj.name === 'LineSegment') as LineSegments)
      : (object.children.find((obj) => obj.name === 'LineSegment') as LineSegments);
  }

  private deleteOutlineObject(object: Load) {
    const line = this.getLine(object);
    if (line === undefined) {
      return;
    }
    const materialLine = line.material as LineBasicMaterial;
    materialLine.color = new THREE.Color(0xffffff);
    materialLine.linewidth = 0.0001;
  }
}
